import React, { useState, useEffect } from "react";
import {
  FaShoppingCart,
  FaTimes,
  FaChevronDown,
  FaSave,
  FaBox,
  FaFilePdf,
} from "react-icons/fa";
import * as cartService from "../../services/cart.service";
import "./Cart.css";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [userCollections, setUserCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Form states for Create Collection
  const [createCollectionForm, setCreateCollectionForm] = useState({
    name: "",
    remark: "",
  });

  // Form states for Update Collection
  const [updateCollectionForm, setUpdateCollectionForm] = useState({
    collectionId: "",
  });

  // Load cart items and collections on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load cart items
      const cartData = await cartService.getShoppingCart();
      setCartItems(cartData);

      // Load user collections
      const collectionsData = await cartService.getUserCollections();
      setUserCollections(collectionsData);

      // Extract unique collections from user collections for backward compatibility
      const uniqueCollections = [];
      const collectionIds = new Set();
      collectionsData.forEach((collection) => {
        if (!collectionIds.has(collection.id)) {
          collectionIds.add(collection.id);
          uniqueCollections.push({
            id: collection.id,
            name: collection.name,
          });
        }
      });
      setCollections(uniqueCollections);
    } catch (error) {
      console.error("Error loading data:", error);
      setMessage("Failed to load cart data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadCartItems = async () => {
    try {
      const cart = await cartService.getShoppingCart();
      setCartItems(cart);
    } catch (error) {
      console.error("Error loading cart:", error);
    }
  };

  const loadCollections = async () => {
    try {
      // Collections are loaded from user collections
      const userCollections = await cartService.getUserCollections();
      const uniqueCollections = [];
      const collectionIds = new Set();

      userCollections.forEach((collection) => {
        if (!collectionIds.has(collection.id)) {
          collectionIds.add(collection.id);
          uniqueCollections.push({
            id: collection.id,
            name: collection.name,
          });
        }
      });

      setCollections(uniqueCollections);
    } catch (error) {
      console.error("Error loading collections:", error);
    }
  };

  const loadUserCollections = async () => {
    try {
      const userCollections = await cartService.getUserCollections();
      setUserCollections(userCollections);
    } catch (error) {
      console.error("Error loading user collections:", error);
    }
  };

  const removeFromCart = async (productUid) => {
    try {
      setIsLoading(true);
      await cartService.removeFromCart(productUid);
      const updated = cartItems.filter((item) => item.uid !== productUid);
      setCartItems(updated);
      setMessage("Item removed from cart");
    } catch (error) {
      console.error("Error removing from cart:", error);
      setMessage("Failed to remove item from cart");
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setIsLoading(true);
      await cartService.clearShoppingCart();
      setCartItems([]);
      setMessage("Cart cleared successfully");
    } catch (error) {
      console.error("Error clearing cart:", error);
      setMessage("Failed to clear cart");
    } finally {
      setIsLoading(false);
    }
  };

  // Generate PDF for selected collection
  const generateCollectionPDF = () => {
    if (!selectedCollection) {
      alert("Please select a collection first");
      return;
    }

    try {
      // Create HTML content for PDF
      let htmlContent = `
        <html>
          <head>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; }
              h1 { color: #333; font-size: 24px; border-bottom: 2px solid #667eea; padding-bottom: 8px; margin: 0 0 10px 0; }
              .collection-info { margin: 10px 0; padding: 10px; background: #f5f7fa; border-radius: 6px; font-size: 12px; }
              .products-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-top: 15px; }
              .product-card { border: 1px solid #ddd; padding: 10px; border-radius: 6px; page-break-inside: avoid; }
              .product-image { max-width: 100%; height: auto; margin-bottom: 8px; border-radius: 4px; }
              .product-item { margin: 3px 0; font-size: 11px; }
              .product-item strong { color: #667eea; font-weight: 600; }
              .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd; color: #666; font-size: 10px; text-align: center; }
              @media print { body { margin: 10px; } .products-grid { gap: 10px; } .product-card { padding: 8px; } }
            </style>
          </head>
          <body>
            <h1>${selectedCollection.name}</h1>
            <div class="collection-info">
              <div class="product-item"><strong>Items:</strong> ${selectedCollection.products.length}</div>
              <div class="product-item"><strong>Date:</strong> ${new Date(selectedCollection.createdAt).toLocaleDateString()}</div>
              ${selectedCollection.remark ? `<div class="product-item"><strong>Remarks:</strong> ${selectedCollection.remark}</div>` : ""}
            </div>
            <div class="products-grid">`;

      // Add products to HTML
      selectedCollection.products.forEach((product) => {
        // Handle both old format (isMain + data) and new format (path)
        let imageSrc = null;
        
        if (product.images && Array.isArray(product.images)) {
          // Try to find image with isMain property (old format)
          const mainImage = product.images.find((img) => img.isMain);
          if (mainImage && mainImage.data) {
            imageSrc = mainImage.data;
          } 
          // Otherwise use first image path (new format from API)
          else if (product.images[0]?.path) {
            imageSrc = product.images[0].path;
          }
        }

        htmlContent += `
          <div class="product-card">
            ${imageSrc ? `<img src="${imageSrc}" class="product-image" style="max-width: 100%;"/>` : `<div style="background: #f0f2ff; height: 60px; display: flex; align-items: center; justify-content: center; border-radius: 4px;">No Image</div>`}
            <div class="product-item"><strong>Item:</strong> ${product.itemNo || "N/A"}</div>
            <div class="product-item"><strong>UID:</strong> ${product.uid || "N/A"}</div>
            ${product.color && product.color.length > 0 ? `<div class="product-item"><strong>Colors:</strong> ${product.color.length}</div>` : ""}
          </div>
        `;
      });

      htmlContent += `
            </div>
            <div class="footer">
              <p>Generated on ${new Date().toLocaleString()}</p>
            </div>
          </body>
        </html>
      `;

      // Open print dialog
      const printWindow = window.open("", "", "width=900,height=700");
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      setTimeout(() => printWindow.print(), 250);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF");
    }
  };

  // Handle Create Collection
  const handleCreateCollection = async () => {
    if (!createCollectionForm.name.trim()) {
      setMessage("Please enter collection name");
      return;
    }

    if (cartItems.length === 0) {
      setMessage("Please add items to cart first");
      return;
    }

    try {
      setIsLoading(true);
      const newCollection = await cartService.createCollection(
        createCollectionForm.name,
        createCollectionForm.remark,
        cartItems
      );

      setMessage(
        `Collection "${createCollectionForm.name}" created successfully with ${cartItems.length} items!`
      );

      // Reset form
      setCreateCollectionForm({ name: "", remark: "" });
      setSelectedAction("");
      setShowActionPanel(false);
      await clearCart();
      
      // Reload user collections
      await loadUserCollections();
    } catch (error) {
      console.error("Error creating collection:", error);
      setMessage("Error creating collection");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Update Collection
  const handleUpdateCollection = async () => {
    if (!updateCollectionForm.collectionId) {
      setMessage("Please select a collection");
      return;
    }

    if (cartItems.length === 0) {
      setMessage("Please add items to cart first");
      return;
    }

    try {
      setIsLoading(true);
      const collection = userCollections.find(
        (c) => c.id === Number(updateCollectionForm.collectionId)
      );

      const updatedCollection = await cartService.updateCollection(
        updateCollectionForm.collectionId,
        cartItems
      );

      setMessage(
        `Added ${cartItems.length} product(s) to collection "${collection?.name}"!`
      );

      // Reset form
      setUpdateCollectionForm({ collectionId: "" });
      setSelectedAction("");
      setShowActionPanel(false);
      await clearCart();
      
      // Reload user collections
      await loadUserCollections();
    } catch (error) {
      console.error("Error updating collection:", error);
      setMessage("Error updating collection");
    } finally {
      setIsLoading(false);
    }
  };

  const renderActionPanel = () => {
    if (!selectedAction) return null;

    switch (selectedAction) {
      case "create-collection":
        return (
          <div className="action-panel">
            <h5>Create New Collection</h5>
            <div className="form-group mb-3">
              <label className="form-label">Collection Name *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter collection name"
                value={createCollectionForm.name}
                onChange={(e) =>
                  setCreateCollectionForm({
                    ...createCollectionForm,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Remark</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Add any remarks..."
                value={createCollectionForm.remark}
                onChange={(e) =>
                  setCreateCollectionForm({
                    ...createCollectionForm,
                    remark: e.target.value,
                  })
                }
              />
            </div>
            <button
              className="btn btn-success"
              onClick={handleCreateCollection}
            >
              <FaSave className="me-2" /> Save Collection
            </button>
          </div>
        );

      case "update-collection":
        return (
          <div className="action-panel">
            <h5>Update Existing Collection</h5>
            <div className="form-group mb-3">
              <label className="form-label">Select Collection *</label>
              <select
                className="form-control"
                value={updateCollectionForm.collectionId}
                onChange={(e) =>
                  setUpdateCollectionForm({
                    ...updateCollectionForm,
                    collectionId: e.target.value,
                  })
                }
              >
                <option value="">-- Select a collection --</option>
                {userCollections.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.name} ({collection.products.length} items)
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-success"
              onClick={handleUpdateCollection}
            >
              <FaSave className="me-2" /> Add to Collection
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="cart-container">
      {/* Header */}
      <div className="cart-header">
        <div className="header-content">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="header-icon">
              <FaShoppingCart size={32} />
            </div>
            <div>
              <h1 className="mb-0">Shopping Cart</h1>
              <p className="mb-0 text-muted">
                {cartItems.length} item{cartItems.length !== 1 ? "s" : ""} in cart
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div
          className="alert alert-info"
          role="alert"
          style={{
            margin: "15px",
            padding: "12px",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{message}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
            aria-label="Close"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="cart-main">
        <div className="cart-content">
          
          {/* Existing Collections Section */}
          {userCollections.length > 0 && (
            <div className="collections-section mb-4">
              <h4 className="section-title">Existing Collections</h4>
              <div className="collection-selector">
                <div className="dropdown-wrapper">
                  <button
                    className="btn btn-outline-primary collection-dropdown-btn"
                    onClick={() => setShowCollectionDropdown(!showCollectionDropdown)}
                  >
                    <span>
                      {selectedCollection
                        ? selectedCollection.name
                        : "Select a Collection"}
                    </span>
                    <FaChevronDown
                      className={`dropdown-icon ${
                        showCollectionDropdown ? "open" : ""
                      }`}
                    />
                  </button>
                  {showCollectionDropdown && (
                    <div className="collection-dropdown-menu show">
                      {userCollections.map((collection) => (
                        <button
                          key={collection.id}
                          className={`collection-dropdown-item ${
                            selectedCollection?.id === collection.id
                              ? "active"
                              : ""
                          }`}
                          onClick={async () => {
                            try {
                              setIsLoading(true);
                              const collectionData = await cartService.getCollection(collection.id);
                              setSelectedCollection(collectionData);
                              setShowCollectionDropdown(false);
                            } catch (error) {
                              console.error("Error loading collection:", error);
                              setMessage("Failed to load collection details");
                            } finally {
                              setIsLoading(false);
                            }
                          }}
                        >
                          <div className="collection-name">{collection.name}</div>
                          <div className="collection-meta">
                            {collection.products.length} items
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Collection View */}
              {selectedCollection && (
                <div className="selected-collection">
                  <div className="collection-header">
                    <div className="collection-info">
                      <h5>{selectedCollection.name}</h5>
                      <p className="text-muted mb-1">
                        {selectedCollection.products.length} product
                        {selectedCollection.products.length !== 1 ? "s" : ""}
                      </p>
                      {selectedCollection.remark && (
                        <p className="text-muted mb-0">
                          <strong>Remark:</strong> {selectedCollection.remark}
                        </p>
                      )}
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={generateCollectionPDF}
                    >
                      <FaFilePdf className="me-2" /> Generate PDF
                    </button>
                  </div>

                  {/* Collection Products Grid */}
                  <div className="collection-products-grid">
                    {selectedCollection.products.map((product) => {
                      // Handle both old format (isMain + data) and new format (path)
                      let imageSrc = null;
                      
                      if (product.images && Array.isArray(product.images)) {
                        // Try to find image with isMain property (old format)
                        const mainImage = product.images.find((img) => img.isMain);
                        if (mainImage && mainImage.data) {
                          imageSrc = mainImage.data;
                        } 
                        // Otherwise use first image path (new format from API)
                        else if (product.images[0]?.path) {
                          imageSrc = product.images[0].path;
                        }
                      }

                      return (
                        <div key={product.uid} className="collection-product-card">
                          <div className="product-image-container">
                            {imageSrc ? (
                              <img
                                src={imageSrc}
                                alt={product.itemNo}
                                className="product-image"
                              />
                            ) : (
                              <div className="product-image-placeholder">
                                <FaBox size={40} className="text-muted" />
                              </div>
                            )}
                          </div>
                          <div className="product-info">
                            <div className="product-item-no">
                              <strong>Item:</strong>
                              <span>{product.itemNo || "N/A"}</span>
                            </div>
                            <p className="product-uid text-muted small">
                              UID: {product.uid || "N/A"}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <FaShoppingCart size={48} className="text-muted mb-3" />
              <h4>Your cart is empty</h4>
              <p className="text-muted">
                Add products from the catalog to get started
              </p>
              <a href="/dashboard/product/catalog" className="btn btn-primary">
                Browse Catalog
              </a>
            </div>
          ) : (
            <div className="cart-items-section">
              {/* Cart Items Grid */}
              <div className="cart-items-grid">
                {cartItems.map((product) => {
                  // Handle both old format (isMain + data) and new format (path)
                  let imageSrc = null;
                  
                  if (product.images && Array.isArray(product.images)) {
                    // Try to find image with isMain property (old format)
                    const mainImage = product.images.find((img) => img.isMain);
                    if (mainImage && mainImage.data) {
                      imageSrc = mainImage.data;
                    } 
                    // Otherwise use first image path (new format from API)
                    else if (product.images[0]?.path) {
                      imageSrc = product.images[0].path;
                    }
                  }

                  return (
                    <div key={product.uid} className="cart-item-card">
                      {/* Product Image */}
                      <div className="product-image-container">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={product.itemNo}
                            className="product-image"
                          />
                        ) : (
                          <div className="product-image-placeholder">
                            <FaBox size={40} className="text-muted" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="product-info">
                        <div className="product-item-no">
                          <strong>Item:</strong>
                          <span>{product.itemNo || "N/A"}</span>
                        </div>
                        <p className="product-uid text-muted small">
                          UID: {product.uid || "N/A"}
                        </p>
                        {product.color && product.color.length > 0 && (
                          <p className="product-specs">
                            <span className="badge bg-secondary">
                              {product.color.length} color
                              {product.color.length > 1 ? "s" : ""}
                            </span>
                          </p>
                        )}
                      </div>

                      {/* Remove Button */}
                      <button
                        className="btn-remove-item"
                        onClick={() => removeFromCart(product.uid)}
                        title="Remove from cart"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Action Dropdown & Panel */}
              <div className="cart-actions-section">
                <div className="action-dropdown">
                  <label className="action-label">Action:</label>
                  <div className="dropdown-wrapper">
                    <button
                      className="btn btn-outline-primary dropdown-btn"
                      onClick={() => setShowActionPanel(!showActionPanel)}
                    >
                      <span>
                        {selectedAction
                          ? selectedAction
                              .split("-")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(" ")
                          : "Select Action"}
                      </span>
                      <FaChevronDown
                        className={`dropdown-icon ${
                          showActionPanel ? "open" : ""
                        }`}
                      />
                    </button>
                    {showActionPanel && (
                      <div className="dropdown-menu show">
                        <button
                          className={`dropdown-item ${
                            selectedAction === "create-collection"
                              ? "active"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedAction("create-collection");
                            setShowActionPanel(false);
                          }}
                        >
                          Create Collection
                        </button>
                        {collections.length > 0 && (
                          <button
                            className={`dropdown-item ${
                              selectedAction === "update-collection"
                                ? "active"
                                : ""
                            }`}
                            onClick={() => {
                              setSelectedAction("update-collection");
                              setShowActionPanel(false);
                            }}
                          >
                            Update Collection
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Panel */}
                {renderActionPanel()}

                {/* Clear Cart Button */}
                <button
                  className="btn btn-outline-danger mt-3"
                  onClick={clearCart}
                >
                  <FaTimes className="me-2" /> Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;
